import PropTypes from 'prop-types'
import { useCallback, useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Dialog, Select, Text } from '@loadsmart/loadsmart-ui'

import { LoadBoardContext, LOAD_BOARD_ACTIONS } from 'legacy/contexts/loadBoard'
import { rejectLoad } from 'legacy/services/loads'
import analytics from 'legacy/utils/analytics'

import { ContractPropType } from './propTypes'
import {
  RejectReasonSeparator,
  StyledTextField,
  StyledButton,
  TextLeftAligned,
} from './RejectLoadModal.styles'

const OTHER_REJECT_REASON = 'other'

const REASON_OPTIONS = [
  {
    label: 'Bad Appointment Times',
    value: 'bad_appointment_times',
  },
  { label: 'No free trucks', value: 'no_free_trucks' },
  { label: 'Wrong area of operation', value: 'wrong_area_of_operation' },
  { label: 'Bad Timing', value: 'bad_timing' },
  { label: 'Other', value: OTHER_REJECT_REASON },
]

function RejectLoadModal({ isOpen, onClose, load }) {
  const [, dispatch] = useContext(LoadBoardContext)
  const { uuid: loadUUID, active_offer: activeOffer } = load || {}
  const { uuid: offerUUID } = activeOffer || {}

  const [isLoading, setIsLoading] = useState(false)
  const [reason, setReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const isCustomReason = reason === OTHER_REJECT_REASON
  const isRejectLoadBtnDisabled = (isCustomReason && !customReason) || (!isCustomReason && !reason)

  useEffect(() => {
    analytics.track('Reject Contracted Load Modal / View', {
      id: load.uuid,
    })
  }, [load])

  function handleCustomReasonChange(event) {
    setCustomReason(event?.target?.value || '')
  }

  function handleReasonChange(e) {
    const selectedOption = e?.target?.value ?? ''
    setCustomReason('')
    setReason(selectedOption.value)
  }

  const handleRejectLoad = useCallback(async () => {
    const payload = {
      offer_id: offerUUID,
      reasons: [isCustomReason ? customReason : reason],
    }
    setIsLoading(true)
    try {
      await rejectLoad(loadUUID, payload)
      toast.error('Contracted load rejected')
      dispatch({
        type: LOAD_BOARD_ACTIONS.MARK_OFFER_TAKEN,
        payload: { loadUUID },
      })
      analytics.track('Reject Contracted Load Modal / Rejected', {
        id: load.uuid,
        customReason,
        reason,
      })
      onClose()
    } catch {
      toast.error('Error rejecting contracted load')
    } finally {
      setIsLoading(false)
    }
  }, [offerUUID, isCustomReason, customReason, reason, loadUUID, dispatch, load, onClose])

  return (
    <Dialog scale="default" open={isOpen} onOverlayClick={onClose}>
      <Dialog.Close onClick={onClose} />
      <Dialog.Header>Confirm the rejection</Dialog.Header>
      <Dialog.Body>
        <Text>
          After confirming and selecting a reason you wont be able to view or accept this load
          again.
        </Text>
        <RejectReasonSeparator>
          <TextLeftAligned as="div">Reason to reject</TextLeftAligned>
          <Select
            placeholder="Select the reason..."
            disabled={isLoading}
            options={REASON_OPTIONS}
            onChange={handleReasonChange}
          />
        </RejectReasonSeparator>
        {isCustomReason && (
          <RejectReasonSeparator>
            <TextLeftAligned as="div">Other reason</TextLeftAligned>
            <StyledTextField
              placeholder="Type here your reason"
              onChange={handleCustomReasonChange}
              value={customReason}
              disabled={isLoading}
            />
          </RejectReasonSeparator>
        )}
        <RejectReasonSeparator>
          <StyledButton
            onClick={handleRejectLoad}
            variant="warning"
            disabled={isLoading || isRejectLoadBtnDisabled}
          >
            Reject Load
          </StyledButton>
        </RejectReasonSeparator>
      </Dialog.Body>
    </Dialog>
  )
}

RejectLoadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  load: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    active_offer: PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      contract: ContractPropType,
    }).isRequired,
    contract: ContractPropType,
  }).isRequired,
}

export { RejectLoadModal }
